import { ScriptOnce } from "@tanstack/solid-router"
import {
  type Accessor,
  createContext,
  createEffect,
  createSignal,
  onCleanup,
  type ParentProps,
  type Setter,
  useContext
} from "solid-js"

type Theme = "dark" | "light" | "system"
const MEDIA = "(prefers-color-scheme: dark)"

type ThemeProviderState = {
  theme: Accessor<Theme>
  setTheme: Setter<Theme>
}

const ThemeProviderContext = createContext<ThemeProviderState>()

const initialThemeScript = `
(function() {
  const storedTheme = localStorage.getItem('theme');
  if (storedTheme) {
    document.documentElement.classList.toggle('dark', storedTheme === 'dark');
  } else {
    document.documentElement.classList.toggle('dark', window.matchMedia('(prefers-color-scheme: dark)').matches);
  }
})();
`

type ThemeProviderProps = ParentProps & {
  defaultTheme?: Theme
  storageKey?: string
}

export const ThemeProvider = (props: ThemeProviderProps) => {
  const storageKey = props.storageKey || "theme"
  const defaultTheme = props.defaultTheme || "system"

  const [theme, setTheme] = createSignal<Theme>(
    (typeof window !== "undefined" ? (localStorage.getItem(storageKey) as Theme) : null) || defaultTheme
  )

  createEffect(() => {
    // Only listen if the current theme is "system"
    if (theme() !== "system") return

    const media = window.matchMedia(MEDIA)

    const applySystemTheme = (e: MediaQueryListEvent | MediaQueryList) => {
      const root = window.document.documentElement
      const targetTheme = e.matches ? "dark" : "light"
      if (!root.classList.contains(targetTheme)) {
        root.classList.remove("light", "dark")
        root.classList.add(targetTheme)
      }
    }

    media.addEventListener("change", applySystemTheme)
    applySystemTheme(media) // Apply immediately on mount/effect run

    onCleanup(() => media.removeEventListener("change", applySystemTheme))
  })

  createEffect(() => {
    const root = window.document.documentElement
    let targetTheme: string

    if (theme() === "system") {
      localStorage.removeItem(storageKey)
      targetTheme = window.matchMedia(MEDIA).matches ? "dark" : "light"
    } else {
      localStorage.setItem(storageKey, theme())
      targetTheme = theme()
    }

    // Only update if the target theme is not already applied
    if (!root.classList.contains(targetTheme)) {
      root.classList.remove("light", "dark")
      root.classList.add(targetTheme)
    }
  })

  const value: ThemeProviderState = {
    theme: theme,
    setTheme: setTheme
  }

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      <ScriptOnce>{initialThemeScript}</ScriptOnce>
      {props.children}
    </ThemeProviderContext.Provider>
  )
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext)

  if (context === undefined) throw new Error("useTheme must be used within a ThemeProvider")

  return context
}
